import { CanvasDatabaseAri } from '@atlassian/ari/canvas/database';
import { ConfluenceSpaceAri } from '@atlassian/ari/confluence/space';
import { YDatabase } from '@databases/collab/y-database';
import { logError } from '@databases/logs';
import { DefaultRootState } from '@databases/redux';
import { IntlShape } from 'react-intl-next';
import {
  unstable_scheduleCallback as scheduleCallback,
  unstable_IdlePriority as idlePriority,
} from 'scheduler';
import { subscribe } from 'valtio';

import { getIdentityClient } from '@canvas/atlassian-client/identity';
import { currentConnectionStatus } from '@canvas/canvas-state';
import {
  AwarenessChannel,
  BrowserWaiter,
  YjsSocket,
  createYjsSocket,
  getDocumentV2,
  getRemoteStepsUpdate,
  persistLargeStep,
} from '@canvas/connection';
import { getIdentity } from '@canvas/connection/identity';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import { Action } from '@canvas/model/permissions';
import { type ClientId } from '@canvas/model/telepointers';
import { TokenManagerV1 } from '@canvas/token-manager';

import { fetchConfluenceData, rejectOnError } from '../../confluence/fetch';
import { DatabaseChangeType, DatabaseChangeset } from '../../database/changesets';

import { addChangeToYjs } from './add-change';
import { migrateJiraServerId } from './migrate-jira-server-id';
import { MigrationManager } from './migrations';
import { YDatabaseTelepointers, TelepointerState } from './ydatabase-telepointers';

type DoSynchronizeOpts = {
  grants: Set<Action>;
  alreadyCreated: boolean;
  canCreate: boolean;
};

export const isSameSet = (a: Set<any>, b: Set<any>) => {
  if (a.size !== b.size) {
    return false;
  }

  for (const aa of a) {
    if (!b.has(aa)) {
      return false;
    }
  }
  return true;
};

const getChangsetTimingStats = (
  keyedTimes: Partial<{ [K in DatabaseChangeType]: number[] }>,
  computeStartTime: number
) => {
  const results: ChangesetTimingStats = {};

  (Object.keys(keyedTimes) as Array<DatabaseChangeType>).forEach((timeKey) => {
    let min = 0;
    let max = 0;
    let avg = 0;
    let index = 0;
    let below50count = 0;

    const times = keyedTimes[timeKey]!;
    times.forEach((time) => {
      if (time !== 0) {
        avg = (avg * index + time) / ++index;
        if (time > max) {
          max = time;
        }
        if (min === 0 || time < min) {
          min = time;
        }
        if (time < 50) {
          below50count++;
        }
      }
    });

    results[timeKey] = {
      min,
      max,
      avg,
      count: index,
      below50count,
      compute: performance.now() - computeStartTime,
    };
  });

  return results;
};

type OnPermissionsChangedHandler = (readOnly: boolean) => void;
type OnErrorHandler = (error: unknown) => void;

type OnLoadedHandler = (db: YDatabase) => void;
type OnTelepointerChangeHandler = (
  clientId: number,
  entryId: TelepointerState | undefined
) => void;

type OnPendingChangesHandler = (pending: boolean) => void;

export type ChangesetTimingStats = Partial<{
  [K in DatabaseChangeType]: {
    min: number;
    max: number;
    avg: number;
    count: number;
    below50count: number;
    compute: number;
  };
}>;

export let onStartChangesetStats: () => void | undefined;
export let onEndChangesetStats: (stats: ChangesetTimingStats) => void | undefined;

export function setOnStartChangesetStats(handler: () => void) {
  onStartChangesetStats = handler;
}

export function setOnEndChangesetStats(handler: (stats: ChangesetTimingStats) => void) {
  onEndChangesetStats = handler;
}

export class CollabManager {
  tokenManager = new TokenManagerV1();
  databaseAri: CanvasDatabaseAri;
  #database: YDatabase;
  #socket: YjsSocket | undefined;
  private grants: Set<Action> = new Set();
  wasCreatedThisSession: boolean;

  /**
   * Mutex for enforcing one synchronization at a time
   */
  private synchronizing: Promise<void> | undefined;

  private onErrorHandlers: Array<OnErrorHandler> = [];

  private onLoadHandlers: Array<OnLoadedHandler> = [];
  private onPendingChangesHandlers: Array<OnPendingChangesHandler> = [];
  private onPermissionsChangedHandlers: Array<OnPermissionsChangedHandler> = [];
  private onTelepointerChangeHandlers: Array<OnTelepointerChangeHandler> = [];

  private times: Partial<{ [K in DatabaseChangeType]: number[] }> = {};
  private telepointers: YDatabaseTelepointers | null = null;

  constructor(
    private cloudId: string,
    private spaceId: string,
    private contentId: string,
    private referenceId: string,
    ydb: YDatabase,
    private intl: IntlShape
  ) {
    this.databaseAri = CanvasDatabaseAri.create({
      siteId: cloudId,
      databaseId: referenceId,
    });

    this.tokenManager.onRefreshToken(async () => {
      return await this.getToken();
    });

    this.tokenManager.onSynchronizeClaims(this.onPermissionsUpdate.bind(this));

    this.#database = ydb;

    this.wasCreatedThisSession = false;
  }

  onLoaded(handler: OnLoadedHandler) {
    this.onLoadHandlers.push(handler);
  }

  onPendingChanges(handler: OnPendingChangesHandler): void {
    this.onPendingChangesHandlers.push(handler);
  }

  onPermissionsChanged(handler: OnPermissionsChangedHandler) {
    this.onPermissionsChangedHandlers.push(handler);
  }

  onError(handler: OnErrorHandler) {
    this.onErrorHandlers.push(handler);
  }

  onTelepointerChange(handler: OnTelepointerChangeHandler) {
    this.onTelepointerChangeHandlers.push(handler);
  }

  updateTelepointer(entryId?: string, fieldId?: string) {
    this.telepointers?.setCursorPosition(entryId, fieldId);
  }

  get database() {
    return this.#database;
  }

  onPermissionsUpdate(
    id: string | undefined,
    opts: {
      board: Set<Action>;
    }
  ) {
    this.onPermissionsChangedHandlers.forEach((handler) =>
      handler(!opts.board.has(Action.write))
    );
    this.synchronize(opts.board);
  }

  private async getToken() {
    const bulkTokens = await (
      await fetchConfluenceData(
        `/rest/internal/1.0/canvas/session/bulk?ids=${this.contentId}`
      ).then(rejectOnError)
    ).json();

    const token = bulkTokens[0].data;

    if (token?.error) {
      logError('Error while fetching token', token.error);
      return;
    }

    const containerAri = ConfluenceSpaceAri.create({
      spaceId: this.spaceId,
      siteId: this.cloudId,
    });

    return {
      container: containerAri.toString(),
      board: this.databaseAri.toString(),
      accessToken: token.access.token,
      mediaToken: token.media.token,
      mediaBaseUrl: token.media.baseUrl,
      mediaCollectionId: token.media.contentId,
    };
  }

  async createSession(grants: Set<Action>) {
    this.#socket = await this.connectSocket(!grants.has(Action.write));
    this.grants = grants;
  }

  killSession() {
    if (this.#socket) {
      this.#socket.destroy();
      this.#socket = undefined;
    }
  }

  async synchronize(grants: Set<Action>) {
    const alreadyCreated = !!this.#socket;
    const canCreate = grants.size > 0;

    // No work if empty grants
    if (grants.size === 0) {
      return;
    }

    try {
      if (this.synchronizing) {
        await this.synchronizing;
        this.synchronizing = undefined;
      }

      const synchronizing = this.doSynchronize({
        grants,
        alreadyCreated,
        canCreate,
      });
      this.synchronizing = synchronizing;

      await this.synchronizing;
      this.synchronizing = undefined;
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch (err) {
      this.onErrorHandlers.forEach((handler) => handler(err));
    }
  }

  private async doSynchronize({ grants, alreadyCreated, canCreate }: DoSynchronizeOpts) {
    if (alreadyCreated) {
      await this.synchronizeGrants(grants);
    } else if (canCreate) {
      await this.createSession(grants);
    }
  }

  private async synchronizeGrants(grants: Set<Action>) {
    // only sync if current grants & pending grants differ
    const grantsSame = isSameSet(this.grants, grants);
    if (grantsSame) {
      return;
    }

    await this.killSession();
    await this.createSession(grants);
  }

  async connect() {
    const token = await this.getToken();
    this.tokenManager.updateToken(token);
  }

  get isReadOnly() {
    return !this.grants.has(Action.write);
  }

  private async connectSocket(isReadOnly: boolean) {
    const metric = ReliabilityMetrics.getInstance().start(MetricName.COLLAB_CONNECT);
    const identityClient = getIdentityClient();
    const serverUrl = `/gateway/api/canvas`;

    const refresher = async () => {
      await this.tokenManager.refreshToken();
    };

    const documentId = this.databaseAri.toString();

    /**
     * A user can go through the sync flow only if they are able to write to a
     * database. Reasoning: The sync protocol involves synchronisation of changes
     * across users, which can lead to changes in a database contents.
     */
    const canSync = !isReadOnly;
    /**
     * A user can propagate updates only if they are allowed to write to a database.
     */
    const canUpdate = !isReadOnly;

    const provider = createYjsSocket({
      relaySupplier: async () => {
        const relayUrl = new URL(process.env.VITE_CANVAS_RELAY_WSS_URL as any);
        relayUrl.pathname = `/api/public/document/${this.referenceId}/ws`;
        relayUrl.searchParams.set('closeOnMissingUpdate', 'true');

        return {
          url: relayUrl.href,
          accessToken: (await this.tokenManager.token()).accessToken,
        };
      },
      identitySupplier: getIdentity,
      userSupplier: identityClient.loadFullIdentityProfiles.bind(identityClient),
      existingDocument: this.#database.doc,
      getDocument: async (signal) =>
        await getDocumentV2({
          documentId,
          serverUrl,
          signal,
          tokenManager: this.tokenManager,
        }),
      boardId: this.referenceId,
      persistLargeStep: (step) =>
        persistLargeStep({
          documentId,
          serverUrl,
          step,
          tokenManager: this.tokenManager,
        }),
      getRemoteStepsUpdate: (headStep, signal) =>
        getRemoteStepsUpdate(
          headStep,
          this.referenceId,
          signal,
          this.tokenManager,
          refresher
        ),
      canSync,
      canUpdate,
      debounceWaiter: new BrowserWaiter(),
    });

    const meta = provider.doc.getMap('_meta');
    const clientDocVersion = MigrationManager.getClientDocVersion();

    provider.addListener('indexedDbSynced', () => {
      const remoteVersion = (meta.get('version') as number | undefined) || 0;

      if (remoteVersion < clientDocVersion) {
        // migrations haven't run or not up to date
        // wait for initialLoad event to trigger migration or update
        return;
      }

      this.onLoadHandlers.map((handler) => handler(this.#database));
    });

    provider.addListener('initialLoad', async () => {
      const remoteVersion = (meta.get('version') as number | undefined) || 0;
      if (remoteVersion === 0) {
        this.wasCreatedThisSession = true;
      }

      const migrationManager = new MigrationManager(
        documentId,
        this.#database,
        this.intl,
        remoteVersion
      );

      await migrationManager.runPending();

      if (remoteVersion < clientDocVersion) {
        meta.set('version', clientDocVersion);
      }

      migrateJiraServerId(documentId, this.#database);

      this.#database.undoManager.clear();
      this.onLoadHandlers.map((handler) => handler(this.#database));
    });

    provider.addListener('connected', () => {
      metric.success();
      currentConnectionStatus.connectionStatus = 'online';
    });

    provider.addListener('fatalError', (error: Error) => {
      metric?.failure();
      logError('Collab connection fatalError', error);
    });

    provider.addListener('failingRetry', (error: Error) => {
      metric?.failure();
      logError('Collab connection failingRetry', error);
    });

    provider.addListener('connecting', (attempt) => {
      if (attempt > 1) {
        currentConnectionStatus.connectionStatus = 'offline';
      }
    });

    provider.addListener('disconnected', () => {
      currentConnectionStatus.connectionStatus = 'offline';
    });

    subscribe(provider.updateTracker.pendingMetadata, () => {
      this.onPendingChangesHandlers.map((handler) =>
        handler(provider.updateTracker.areChangesPending)
      );
    });

    this.telepointers = new YDatabaseTelepointers(
      provider.awarenessContainer.getAwareness(AwarenessChannel.Telepointer),
      getIdentity,
      identityClient.loadFullIdentityProfiles.bind(identityClient)
    );
    subscribe(this.telepointers.telepointers, (ops) => {
      ops
        .flatMap(([_, clientIds]) => clientIds as unknown as ClientId[])
        .forEach((clientId: ClientId) => {
          this.onTelepointerChangeHandlers.forEach((handler) => {
            handler(clientId, this.telepointers?.telepointers[clientId]);
          });
        });
    });

    return provider;
  }

  tsubmit: NodeJS.Timeout | undefined;
  end = () => {
    const timesCopy = { ...this.times };
    this.times = {};
    scheduleCallback(idlePriority, () => {
      const computeStartTime = performance.now();
      const data = getChangsetTimingStats(timesCopy, computeStartTime);
      onEndChangesetStats?.(data);
    });
  };

  applyChangeset(changeset: DatabaseChangeset, state: DefaultRootState | undefined) {
    onStartChangesetStats?.();
    for (const change of changeset) {
      const startTime = performance.now();
      addChangeToYjs(this.#database, change, state);
      const endTime = performance.now();
      const time = endTime - startTime;
      if (!this.times[change.type]) {
        this.times[change.type] = [];
      }

      this.times[change.type]!.push(time);
    }

    setTimeout(() => {
      clearTimeout(this.tsubmit);
      this.tsubmit = setTimeout(this.end, 2000); // debounce
    }, 0);
  }

  /**
   * Calls the given callback, grouping any YJS mutations that happen during its execution into a single
   * YJS transaction. This can be useful to make the undo manager register the change as a single action.
   * Does not support async callbacks.
   */
  batch(callback: () => void): void {
    this.#database.mutate(callback);
  }
}
